<script>
import * as moment from 'moment'
import config from '@/config';

export default {
  props: {
    polygons: { type: Array, default: () => [] },
  },

  data: () => ({
    lastPositionAndTime: undefined,
    mapId: undefined,
    errorHtml: undefined,
    watchGeolocationId: undefined,
    map: undefined,
    currentMarker: undefined,

    mapPolygons: [],
  }),

  watch: {
    polygons: {
      handler: function (to, from) {
        this.reAddPolygonsToMap();
      },
      deep: true,
    }
  },

  created() {
    this.mapId = `map-comp-${this.newGuid()}`;
  },

  mounted() {
    L.mapquest.key = config.MapQuest.ApiKey;

    if (navigator.geolocation) {
      this.retryGetInitialPosition();
    } else {
      this.errorHtml = `Error: Your browser doesn't support geolocation.`;
    }
  },

  destroyed() {
    if (navigator.geolocation && this.watchGeolocationId) {
      navigator.geolocation.clearWatch(this.watchGeolocationId);
    }
  },

  methods: {
    retryGetInitialPosition() {
      if (navigator.geolocation && this.watchGeolocationId) {
        navigator.geolocation.clearWatch(this.watchGeolocationId);
      }

      const timeoutSeconds = 5;
      const retryDelaySeconds = 5;

      let positionOptions = {
        //TODO: Timeout and max age could be customizable by user?
        timeout: timeoutSeconds * 1000,
        enableHighAccuracy: true,
        maximumAge: 10 * 1000, //Infinity,
      };

      this.watchGeolocationId = navigator.geolocation.watchPosition(
        (position) => {
          this.errorHtml = undefined;
          this.lastPositionAndTime = `${Number(position.coords.latitude).toFixed(6)}, ${Number(position.coords.longitude).toFixed(6)} updated at ${moment(position.timestamp).format('YYYY-MM-DD HH:mm:ss')}`;
          this.$nextTick(() => {
            this.onPositionChanged(position);
          });
        }, (error) => {
          console.error(`Got error in geolocation.watchPosition, error '${error.message}', code ${error.code}. Will now auto-retry to get position.`);
          let now = moment().format('YYYY-MM-DD HH:mm:ss');
          let userErr = this.getGeolocationUserError(error, timeoutSeconds);
          this.errorHtml = `<span style="color:grey">[${now}]</span> <br>${userErr}<br>We will keep on retrying every ${retryDelaySeconds} seconds in the meanwhile.`;

          setTimeout(() => {
            this.retryGetInitialPosition();
          }, retryDelaySeconds * 1000);
        }, positionOptions)
    },
    getGeolocationUserError(error, timeoutSeconds) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          return `GPS location is denied or disabled.<br>Please ensure your <strong>Location permission is enabled (allowed)</strong> on both your Device and Internet Browser.`;
        case error.POSITION_UNAVAILABLE:
          return `Location information is unavailable at the moment, please try again or contact the site administrator.`;
        case error.TIMEOUT:
          return `The request to get user location took longer than expected and timed out (after ${timeoutSeconds} seconds).`;
        case error.UNKNOWN_ERROR:
          return `Unknown error '${error.message}' (code ${error.code}).`;
      }
      return `We did not recognize the error '${error.message}' (code ${error.code}).`;
    },
    initMap(lat, lon, timestamp) {
      //let defaultLayer = L.mapquest.tileLayer('hybrid', { keepBuffer: 2000 }); //need to reuse the same instance otherwise not selected in Layers on UI
      let defaultLayer = L.mapquest.tileLayer('hybrid'); //need to reuse the same instance otherwise not selected in Layers on UI

      this.map = L.mapquest.map(this.mapId, {
        center: [lat, lon],
        layers: defaultLayer,
        zoom: 15
      });

      L.control.layers({
        'Map': L.mapquest.tileLayer('map'),
        'Hybrid': defaultLayer,
        'Satellite': L.mapquest.tileLayer('satellite'),
        'Light': L.mapquest.tileLayer('light'),
        'Dark': L.mapquest.tileLayer('dark')
      }).addTo(this.map);

      this.$emit('initialized', {
        lat: lat,
        lon: lon,
        time: timestamp,
      });
    },

    onPositionChanged(position) {
      if (!this.currentMarker) {
        // first time

        this.initMap(position.coords.latitude, position.coords.longitude, position.timestamp);

        this.currentMarker = L.marker([position.coords.latitude, position.coords.longitude], {
          icon: L.mapquest.icons.marker(),
          draggable: false
        }).bindPopup('Your current position');
        this.currentMarker.addTo(this.map);
      } else {
        this.currentMarker.setLatLng([position.coords.latitude, position.coords.longitude]);
      }

      this.$emit('position-changed', {
        lat: position.coords.latitude,
        lon: position.coords.longitude,
        time: position.timestamp,
      });
    },

    newGuid() {
      //https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
      function _p8(s) {
        var p = (Math.random().toString(16) + '000000000').substr(2, 8);
        return s ? '-' + p.substr(0, 4) + '-' + p.substr(4, 4) : p;
      }
      return _p8() + _p8(true) + _p8(true) + _p8();
    },

    reAddPolygonsToMap() {
      if (!this.map) return;

      while (this.mapPolygons.length > 0) {
        let polygon = this.mapPolygons.pop();
        polygon.remove();
      }

      if (!this.polygons) return;

      this.polygons.forEach(pol => {
        let tmpPolygon = L.polygon(pol.points, { color: pol.color || 'red' });
        if (pol.popup) {
          tmpPolygon.bindPopup(pol.popup);
        }
        tmpPolygon.addTo(this.map);
        this.mapPolygons.push(tmpPolygon);
      });

      let firstSelected = this.polygons.find(pol => pol.isSelected);
      if (firstSelected && firstSelected.points && firstSelected.points.length > 0) {
        this.zoomToPolygons([firstSelected]);
      } else if (this.polygons.length > 0 && this.polygons.find(pol => pol.points && pol.points.length > 0)) {
        this.zoomToPolygons(this.polygons);
      }
    },

    zoomToPolygons(polygons) {
      //https://leafletjs.com/reference-1.3.2.html#latlngbounds
      // this.map.fitBounds(polygons.map(pol => pol.points));
      this.map.flyToBounds(polygons.map(pol => pol.points), { duration: 0.75 });
      // this.map.panInsideBounds(polygons.map(pol => pol.points));
    },
  },
}
</script>

<template>
  <div class="card">
    <div class="card-body"
      layout="column">
      <div v-if="$debugMode"
        class="card">
        <div class="card-body">
          <div v-if="lastPositionAndTime"
            class="text-muted"
            style="margin-top:10px; font-size: 0.75em;">Last pos: {{lastPositionAndTime}}</div>
        </div>
      </div>

      <div v-if="!map"
        class="initializing-msg">
        <div class="text-center">Initializing map</div>
        <div class="progress">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
            style="width: 100%"></div>
        </div>
      </div>

      <div v-if="errorHtml" class="text-danger mt-2" v-html="errorHtml"></div>
      <div v-else :id="mapId" flex class="map"> </div>

    </div>
  </div>
</template>

<style scoped>
.initializing-msg {
  margin-top: 15px;
}
</style>



// WEBPACK FOOTER //
// src/components/InteractiveMap.vue